import { useState } from "react";
import Layout from "../../../components/Layout";
import { HiSearch } from "react-icons/hi";
import {
  CustomTable, CustomTableHead, CustomTableBody,
  CustomTableHeadCell, CustomTableRow, CustomTableCell,
} from "../../../components/CustomTable";
import { CustomInput } from "../../../components/FormFields";
import { StatusBadge } from "../../../components/StatusBadge";

const employees = [
  {
    id: "EMP001", name: "Mr. John A. Smith",  category: "Senior Staff",    policy: "Permanent Staff Policy",
    entitlements: [
      { name: "Annual Leave",    total: 21, used: 5  },
      { name: "Sick Leave",      total: 14, used: 2  },
      { name: "Casual Leave",    total: 7,  used: 1  },
      { name: "No-Pay Leave",    total: 0,  used: 3  },
      { name: "Maternity Leave", total: 84, used: 0  },
    ],
  },
  {
    id: "EMP002", name: "Ms. Priya Nair",      category: "Junior Staff",    policy: "Permanent Staff Policy",
    entitlements: [
      { name: "Annual Leave",    total: 21, used: 14 },
      { name: "Sick Leave",      total: 14, used: 7  },
      { name: "Casual Leave",    total: 7,  used: 3  },
      { name: "No-Pay Leave",    total: 0,  used: 0  },
      { name: "Maternity Leave", total: 84, used: 84 },
    ],
  },
  {
    id: "EMP003", name: "Mr. Kasun Perera",    category: "Temporary Staff", policy: "Temporary Staff Policy",
    entitlements: [
      { name: "Casual Leave",  total: 5, used: 2 },
      { name: "No-Pay Leave",  total: 0, used: 1 },
    ],
  },
  {
    id: "EMP004", name: "Ms. Dilini Fernando", category: "Senior Staff",    policy: "Permanent Staff Policy",
    entitlements: [
      { name: "Annual Leave",    total: 21, used: 0 },
      { name: "Sick Leave",      total: 14, used: 4 },
      { name: "Casual Leave",    total: 7,  used: 0 },
      { name: "No-Pay Leave",    total: 0,  used: 0 },
      { name: "Maternity Leave", total: 84, used: 0 },
    ],
  },
  {
    id: "EMP005", name: "Mr. Ravi Kumar",      category: "Intern",          policy: "Intern Policy",
    entitlements: [
      { name: "Casual Leave", total: 3, used: 1 },
    ],
  },
];

function usageStatus(used, total) {
  if (total === 0) return { color: "gray",    label: "Unlimited" };
  if (used >= total) return { color: "failure", label: "Exhausted" };
  if (used / total >= 0.75) return { color: "warning", label: "Low"       };
  return { color: "success", label: "Available" };
}

const CAT_COLOR = {
  "Senior Staff":    "purple",
  "Junior Staff":    "blue",
  "Temporary Staff": "warning",
  "Intern":          "indigo",
  "Probation":       "gray",
};

export default function LeaveEntitlement() {
  const [search, setSearch]     = useState("");
  const [selected, setSelected] = useState(null);

  const filtered = employees.filter((e) =>
    e.name.toLowerCase().includes(search.toLowerCase()) ||
    e.id.toLowerCase().includes(search.toLowerCase()) ||
    e.category.toLowerCase().includes(search.toLowerCase())
  );

  const detail = selected ?? filtered[0] ?? null;

  return (
    <Layout>
      <div className="flex flex-col gap-6">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Leave Entitlements</h1>
          <p className="mt-1 text-sm text-gray-500">
            View each employee's annual leave balance — auto-assigned from their policy
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">

          {/* ── Left: Employee list ── */}
          <div className="flex flex-col gap-3">
            <CustomInput
              icon={HiSearch}
              placeholder="Search employee..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setSelected(null); }}
            />

            <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm dark:border-gray-700 dark:bg-gray-800">
              {filtered.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-400">No employees found.</p>
              ) : filtered.map((emp) => {
                const isActive = detail?.id === emp.id;
                return (
                  <button
                    key={emp.id}
                    type="button"
                    onClick={() => setSelected(emp)}
                    className={`w-full flex flex-col gap-0.5 px-4 py-3 text-left transition-colors border-b border-gray-100 last:border-0 dark:border-gray-700 ${
                      isActive
                        ? "bg-purple-50 dark:bg-purple-900/20"
                        : "hover:bg-gray-50 dark:hover:bg-gray-700"
                    }`}
                  >
                    <span className={`text-sm font-medium ${isActive ? "text-purple-700 dark:text-purple-300" : "text-gray-800 dark:text-gray-200"}`}>
                      {emp.name}
                    </span>
                    <span className="text-xs text-gray-400">{emp.id} · {emp.category}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Right: Detail panel ── */}
          {detail ? (
            <div className="lg:col-span-2 flex flex-col gap-4">

              {/* Employee info card */}
              <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-gray-800 dark:text-white">{detail.name}</p>
                    <p className="text-sm text-gray-400">{detail.id}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <StatusBadge color={CAT_COLOR[detail.category] ?? "gray"} className="text-xs">
                      {detail.category}
                    </StatusBadge>
                    <StatusBadge color="indigo" className="text-xs">{detail.policy}</StatusBadge>
                  </div>
                </div>
              </div>

              {/* Balances table */}
              <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Leave Balances — {new Date().getFullYear()}
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <CustomTable>
                    <CustomTableHead>
                      <CustomTableRow>
                        <CustomTableHeadCell>Leave Type</CustomTableHeadCell>
                        <CustomTableHeadCell className="text-center">Entitled</CustomTableHeadCell>
                        <CustomTableHeadCell className="text-center">Used</CustomTableHeadCell>
                        <CustomTableHeadCell className="text-center">Remaining</CustomTableHeadCell>
                        <CustomTableHeadCell>Status</CustomTableHeadCell>
                      </CustomTableRow>
                    </CustomTableHead>
                    <CustomTableBody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {detail.entitlements.map((ent) => {
                        const remaining = ent.total === 0 ? "∞" : Math.max(0, ent.total - ent.used);
                        const { color, label } = usageStatus(ent.used, ent.total);
                        return (
                          <CustomTableRow key={ent.name} className="hover:bg-purple-50 dark:hover:bg-gray-700">
                            <CustomTableCell className="text-sm font-medium text-gray-700 dark:text-gray-200">
                              {ent.name}
                            </CustomTableCell>
                            <CustomTableCell className="text-center text-sm text-gray-500 dark:text-gray-400">
                              {ent.total === 0 ? "∞" : ent.total}
                            </CustomTableCell>
                            <CustomTableCell className="text-center text-sm text-gray-500 dark:text-gray-400">
                              {ent.used}
                            </CustomTableCell>
                            <CustomTableCell className="text-center text-sm font-semibold text-gray-800 dark:text-gray-200">
                              {remaining}
                            </CustomTableCell>
                            <CustomTableCell>
                              <StatusBadge color={color} className="text-xs w-fit">{label}</StatusBadge>
                            </CustomTableCell>
                          </CustomTableRow>
                        );
                      })}
                    </CustomTableBody>
                  </CustomTable>
                </div>
              </div>

            </div>
          ) : (
            <div className="lg:col-span-2 flex items-center justify-center rounded-xl border border-dashed border-gray-200 dark:border-gray-600 py-24">
              <p className="text-sm text-gray-400">Select an employee to view their leave balances</p>
            </div>
          )}

        </div>
      </div>
    </Layout>
  );
}
